import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useApiClient } from '../hooks/useApiClient';

const GitHubAppCallback: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
    const [message, setMessage] = useState('Processing GitHub App installation...');
    const [processed, setProcessed] = useState(false);

    const apiClient = useApiClient();
    
    const finalizeInstallation = useMutation({
        mutationFn: async (data: { installationId: string; name: string; description?: string; setupAction?: string }) => {
            const response = await apiClient.post('/integrations/github/callback', data);
            
            if (!response.ok) {
                throw new Error(`Failed to finalize installation: ${response.statusText}`);
            }
            
            return response.json();
        }
    });

    useEffect(() => {
        if (processed) return;
        
        const processCallback = async () => {
            try {
                setProcessed(true);
                
                const installationId = searchParams.get('installation_id');
                const setupAction = searchParams.get('setup_action');
                const stateParam = searchParams.get('state');
                
                if (!installationId) {
                    throw new Error('Installation ID not found in callback');
                }
                
                if (!stateParam) {
                    throw new Error('Connection details not found in state parameter');
                }
                
                // Parse connection details from state
                const connectionDetails = JSON.parse(decodeURIComponent(stateParam));
                const { name, description } = connectionDetails;
                
                if (!name) {
                    throw new Error('Connection name not found in state');
                }
                
                // Finalize the installation
                await finalizeInstallation.mutateAsync({
                    installationId,
                    name,
                    description,
                    setupAction: setupAction || undefined
                });
                
                setStatus('success');
                setMessage('GitHub App installation completed successfully!');
                
                // Redirect to GitHub App management page after 2 seconds
                setTimeout(() => {
                    navigate('/configurations/connections/github-app');
                }, 2000);
                
            } catch (error) {
                console.error('GitHub App installation callback error:', error);
                setStatus('error');
                setMessage(`Installation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
                
                // Redirect back after 3 seconds
                setTimeout(() => {
                    navigate('/configurations/connections/github-app');
                }, 3000);
            }
        };

        processCallback();
    }, [searchParams, navigate, processed, finalizeInstallation]);

    return (
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '50vh',
            padding: '20px'
        }}>
            <div style={{ 
                textAlign: 'center',
                maxWidth: '500px'
            }}>
                {status === 'processing' && (
                    <div>
                        <div style={{ marginBottom: '20px' }}>⏳</div>
                        <h2>Processing Installation</h2>
                        <p>{message}</p>
                    </div>
                )}
                
                {status === 'success' && (
                    <div>
                        <div style={{ marginBottom: '20px', fontSize: '48px' }}>✅</div>
                        <h2>Installation Successful</h2>
                        <p>{message}</p>
                        <p>Redirecting you back...</p>
                    </div>
                )}
                
                {status === 'error' && (
                    <div>
                        <div style={{ marginBottom: '20px', fontSize: '48px' }}>❌</div>
                        <h2>Installation Failed</h2>
                        <p style={{ color: 'red' }}>{message}</p>
                        <p>Redirecting you back...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GitHubAppCallback;